/* ─────────────────────────────────────────────────────────────
   AskFloatingButton — 56×56 circular FAB fixed at bottom-right.
   Shows Sparkles icon when closed, X icon when open.
   Ctrl+J / Cmd+J shortcut toggles the popover (remapped from ⌘K in ENT-60-T02).
   ⌘K is now reserved for Global Search (GlobalSearchPalette).
   ───────────────────────────────────────────────────────────── */

import { useEffect } from 'react'
import { Sparkles, X } from 'lucide-react'
import { cn } from '@/lib/cn'

export interface AskFloatingButtonProps {
  isOpen: boolean
  onClick: () => void
  className?: string
}

export function AskFloatingButton({ isOpen, onClick, className }: AskFloatingButtonProps) {
  // Ctrl+J / Cmd+J keyboard shortcut (remapped from ⌘K in ENT-60-T02; ⌘K → Global Search)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'j') {
        e.preventDefault()
        onClick()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClick])

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={isOpen ? 'Close Ask assistant' : 'Open Ask assistant'}
      aria-expanded={isOpen}
      aria-haspopup="dialog"
      className={cn(
        'fixed bottom-5 right-5 z-30',
        'w-14 h-14 rounded-full',
        'flex items-center justify-center',
        'shadow-float transition-all duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2',
        isOpen
          ? 'bg-surface-muted text-text-muted hover:bg-surface-high'
          : 'bg-primary text-primary-foreground hover:opacity-90',
        className,
      )}
    >
      {isOpen ? (
        <X size={22} aria-hidden />
      ) : (
        <Sparkles size={22} aria-hidden />
      )}
    </button>
  )
}
